import React, { createRef } from 'react'
import PropTypes from 'prop-types'
import { Select } from 'antd'
import strings from 'utils/strings'
import { classNames, isEmptyString } from 'utils/utils'
import { isArray, isString, isObject, isNumber } from 'utils/types'
import { isEmpty, either, both, propEq, complement, always, isNil } from 'ramda'
import InputLabel from 'components/patterns/Inputs/InputLabel'
import {
  ErrorMessage,
  SelectMenuStyled,
} from 'components/deprecated/Inputs/Input.style'

import 'styles/AntOverrides.css'
import 'styles/ChecComponents.css'

class SelectMenu extends React.Component {
  state = {
    isDirty: false,
  }

  containingEl = createRef()

  static propTypes = {
    className: PropTypes.string,
    mode: PropTypes.oneOf(['default', 'multiple', 'tags']),
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.arrayOf(PropTypes.number),
    ]),
    defaultValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.arrayOf(PropTypes.number),
    ]),
    optionLabelProp: PropTypes.string,
    optionValueProp: PropTypes.string,
    placeholder: PropTypes.string,
    size: PropTypes.oneOf(['small', 'large', 'default']),
    onChange: PropTypes.func,
    onSelect: PropTypes.func,
    children: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.arrayOf(PropTypes.element),
    ]),
    showSearch: PropTypes.bool,
    showArrow: PropTypes.bool,
    style: PropTypes.object,
    dropdownClassName: PropTypes.string,
    optionFilterProp: PropTypes.string,
    allowClear: PropTypes.bool,
    filterOption: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    required: PropTypes.bool,
    onValidityChange: PropTypes.func,
    customErrorMessage: PropTypes.string,
    options: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.object),
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.arrayOf(PropTypes.number),
    ]),
    label: PropTypes.string,
    vertical: PropTypes.bool,
    verticalStyle: PropTypes.object,
    autoWidthItems: PropTypes.bool,
    dropdownRender: PropTypes.func,
    onSearch: PropTypes.func,
    onInputKeyDown: PropTypes.func,
    onBlur: PropTypes.func,
    mask: PropTypes.func,
    tooltip: PropTypes.string,
    hint: PropTypes.string,
    forceValidation: PropTypes.bool,
    'data-testid': PropTypes.string,
    getPopupContainer: PropTypes.func,
  }

  static defaultProps = {
    style: {},
    className: '',
    options: [],
    mask: null,
    forceValidation: true,
  }

  onChange = (value, option) => {
    this.setState({ isDirty: true }, () => {
      if (this.props.onChange) {
        this.props.onChange(value, option)
      }
    })
  }

  onSelect = (value, option) => {
    this.setState({ isDirty: true }, () => {
      if (this.props.onSelect) {
        this.props.onSelect(value, option)
      }
    })
  }

  componentDidMount() {
    const { customErrorMessage, onValidityChange } = this.props
    if (customErrorMessage) {
      onValidityChange(false, customErrorMessage)
    }
  }

  // Relying on componentDidUpdate to allow us to detect and hande programmatic
  // (not directly user-controlled) updates appropriately.
  componentDidUpdate(prevProps) {
    const {
      onValidityChange,
      required,
      value,
      customErrorMessage,
      forceValidation,
    } = this.props

    // Becomes dirty upon *any* change to props
    if (!this.state.isDirty) {
      this.setState({ isDirty: true })
    }

    const propNotEq = complement(propEq)
    const runValidation = either(
      propNotEq('value', value),
      both(
        always(forceValidation),
        propNotEq('forceValidation', forceValidation)
      )
    )(prevProps)
    // if the component is required to have a value (via 'required' prop),
    // and there is a handler for the change from a valid (value provided) state
    // to invalid, then we fire the handler
    if (runValidation && required && onValidityChange) {
      const isProvided = !!value
      const wasProvided = !!prevProps.value
      if ((wasProvided && !isProvided) || (!wasProvided && isProvided)) {
        onValidityChange(isProvided)
      }
    }

    if (customErrorMessage !== prevProps.customErrorMessage) {
      onValidityChange(
        !customErrorMessage,
        customErrorMessage || prevProps.customErrorMessage
      )
    }
  }

  render() {
    const {
      showSearch,
      filterOption,
      showArrow,
      style,
      dropdownClassName,
      optionFilterProp,
      allowClear,
      value,
      defaultValue,
      optionLabelProp,
      placeholder,
      size,
      options,
      className,
      mode,
      required,
      label,
      vertical,
      verticalStyle,
      autoWidthItems,
      customErrorMessage,
      optionValueProp,
      dropdownRender,
      onSearch,
      onInputKeyDown,
      onBlur,
      mask,
      hint,
      tooltip,
      forceValidation,
      'data-testid': dataTestId,
      getPopupContainer,
    } = this.props

    // if value is null or empty string '', show the placeholder
    const computeValue =
      isEmptyString(value) || isNil(value) ? undefined : value
    const disabled = isEmpty(options) || !options
    const placeholderWithDefault = disabled
      ? strings.no_input_available
      : placeholder

    const handleMask = (text, value) => {
      if (!mask) return text
      return mask(text, value)
    }

    return (
      <SelectMenuStyled
        className={classNames('form-field', className)}
        style={vertical && verticalStyle ? verticalStyle : {}}
        ref={this.containingEl}
      >
        {label && (
          <InputLabel
            label={label}
            hint={hint}
            tooltip={tooltip}
            required={required}
            size={size}
            vertical={vertical}
          />
        )}

        <Select
          disabled={disabled}
          className={classNames('chec-select-menu', {
            'has-error':
              customErrorMessage || (forceValidation && !value && required),
          })}
          filterOption={filterOption}
          showArrow={showArrow}
          style={style}
          dropdownClassName={classNames(dropdownClassName, {
            'select-dropdown-auto-width': autoWidthItems,
          })}
          optionFilterProp={optionFilterProp}
          allowClear={allowClear}
          showSearch={showSearch}
          value={computeValue}
          defaultValue={defaultValue}
          optionLabelProp={optionLabelProp}
          placeholder={placeholderWithDefault}
          size={size}
          onChange={this.onChange}
          onSelect={this.onSelect}
          mode={mode}
          getPopupContainer={
            getPopupContainer || (() => this.containingEl.current)
          }
          dropdownRender={dropdownRender}
          onSearch={onSearch}
          onInputKeyDown={onInputKeyDown}
          onBlur={onBlur}
          dropdownMatchSelectWidth={false}
          notFoundContent={''}
          data-testid={dataTestId}
        >
          {options &&
            options.map((opt) => {
              if (
                isObject(opt) &&
                optionLabelProp &&
                optionValueProp &&
                !opt.groupName
              ) {
                const labelProp = {
                  [optionLabelProp]: opt[optionLabelProp],
                }
                return (
                  <Select.Option
                    value={opt[optionValueProp]}
                    key={opt[optionValueProp]}
                    {...labelProp}
                  >
                    {handleMask(opt[optionLabelProp], value)}
                  </Select.Option>
                )
              }
              if (isString(opt) || isNumber(opt)) {
                return (
                  <Select.Option
                    key={opt}
                    title={`${opt}`}
                    value={opt}
                    className=""
                  >
                    {handleMask(opt, value)}
                  </Select.Option>
                )
              }
              const {
                key,
                title,
                value: optValue,
                image,
                className,
                disabled,
                groupName,
                children,
              } = opt

              return groupName && isArray(children) ? (
                <Select.OptGroup key={key || groupName} label={groupName}>
                  {children.map((child) => {
                    const childKey = optionValueProp
                      ? child[optionValueProp]
                      : child.key
                    const childValue = optionValueProp
                      ? child[optionValueProp]
                      : child.value
                    const childTitle = optionLabelProp
                      ? child[optionLabelProp]
                      : child.title
                    return (
                      <Select.Option
                        key={childKey}
                        title={childTitle}
                        value={childValue}
                        className={child.className}
                        disabled={child.disabled}
                      >
                        {handleMask(childTitle || childValue, value)}
                      </Select.Option>
                    )
                  })}
                </Select.OptGroup>
              ) : (
                <Select.Option
                  key={key || optValue}
                  title={title}
                  value={optValue}
                  className={className}
                  disabled={disabled}
                >
                  {handleMask(image || title || optValue, optValue || value)}
                </Select.Option>
              )
            })}
        </Select>
        {forceValidation &&
          !customErrorMessage &&
          required &&
          this.state.isDirty &&
          !value && (
            <ErrorMessage className="error-msg">
              {strings.required}
            </ErrorMessage>
          )}
        {customErrorMessage && (
          <ErrorMessage className="error-msg">
            {customErrorMessage}
          </ErrorMessage>
        )}
      </SelectMenuStyled>
    )
  }
}

export default SelectMenu
